// ============================================================
// ByOurConsent.com -- Follow the Money
// /premium/follow-the-money
// Premium member tool -- Official Accountability Suite
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { downloadReportAsWord } from '../utils/downloadRtf';

const US_STATES = [
    'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
    'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
    'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
    'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
    'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
    'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
    'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
    'Wisconsin','Wyoming'
];

const SUBJECT_TYPES = [
    { id: 'elected_official', label: 'Elected official', desc: 'Senator, representative, mayor, council member, etc.' },
    { id: 'appointed_official', label: 'Appointed official or agency head', desc: 'Cabinet member, agency director, commissioner, etc.' },
    { id: 'government_agency', label: 'Government agency or department', desc: 'How an agency spends money and awards contracts' },
    { id: 'contractor', label: 'Government contractor', desc: 'A company or individual receiving government contracts' },
    { id: 'lobbyist', label: 'Lobbyist or lobbying firm', desc: 'Who they represent and what they spend' },
];

const INVESTIGATION_TYPES = [
    { id: 'campaign_finance', label: 'Campaign finance & donors', desc: 'Who funds their campaigns, PAC connections, dark money' },
    { id: 'contracts', label: 'Government contracts', desc: 'Contracts awarded, to whom, for how much, and why' },
    { id: 'disclosures', label: 'Personal financial disclosures', desc: 'Personal assets, investments, income sources, conflicts' },
    { id: 'spending', label: 'Government spending & budget', desc: 'How agency funds are allocated and spent' },
    { id: 'lobbying', label: 'Lobbying activity & influence', desc: 'Lobbyist contacts, spending, revolving door connections' },
    { id: 'travel', label: 'Travel & expense records', desc: 'Publicly funded travel, gifts, and expenses' },
];

const StepIndicator = ({ step, total, onStepClick }) => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2rem' }}>
        {Array.from({ length: total }, (_, i) => (
            <React.Fragment key={i}>
                <div onClick={() => i + 1 < step && onStepClick && onStepClick(i + 1)}
                    title={i + 1 < step ? 'Click to go back' : ''}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', background: i + 1 <= step ? '#8b1a1a' : '#ede8dc', border: `0.5px solid ${i + 1 <= step ? '#8b1a1a' : '#c8beaa'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600, color: i + 1 <= step ? '#f5f0e8' : '#8a8070', cursor: i + 1 < step ? 'pointer' : 'default' }}>
                    {i + 1 < step ? '✓' : i + 1}
                </div>
                {i < total - 1 && <div style={{ flex: 1, height: '1px', background: i + 1 < step ? '#8b1a1a' : '#c8beaa' }} />}
            </React.Fragment>
        ))}
    </div>
);

const iStyle = { width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '8px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' };
const lStyle = { fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' };
const btnPrimary = { background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: '3px', cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#4a5060', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '13px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' };

const FollowTheMoneyPage = () => {
    const [step,    setStep]    = useState(1);
    const [loading, setLoading] = useState(false);
    const [result,  setResult]  = useState(null);
    const [error,   setError]   = useState('');

    const [form, setForm] = useState({
        subjectName:       '',
        subjectType:       '',
        level:             'federal',
        state:             '',
        investigationTypes: [],
        context:           '',
        specificConcern:   ''
    });

    const set = (field, value) => setForm(p => ({ ...p, [field]: value }));
    const toggleInv = (id) => setForm(p => ({ ...p, investigationTypes: p.investigationTypes.includes(id) ? p.investigationTypes.filter(i => i !== id) : [...p.investigationTypes, id] }));

    const selectedSubjectType = SUBJECT_TYPES.find(s => s.id === form.subjectType);
    const canProceed = form.subjectName && form.subjectType && form.investigationTypes.length > 0;

    const generate = async () => {
        setLoading(true);
        setError('');

        const investigations = INVESTIGATION_TYPES.filter(i => form.investigationTypes.includes(i.id)).map(i => i.label).join(', ');
        const levelDesc = form.level === 'federal' ? 'Federal' : `${form.state} State`;

        const prompt = `You are an expert in government financial transparency, campaign finance law, public records, and investigative research. Provide a comprehensive financial investigation roadmap.

SUBJECT OF INVESTIGATION:
- Name: ${form.subjectName}
- Type: ${selectedSubjectType?.label}
- Level: ${levelDesc}
${form.context ? `- Context: ${form.context}` : ''}
${form.specificConcern ? `- Specific concern: ${form.specificConcern}` : ''}

AREAS TO INVESTIGATE: ${investigations}

Provide a comprehensive financial investigation guide:

## WHO AND WHAT YOU ARE INVESTIGATING
- Brief overview of what financial records are relevant for a ${selectedSubjectType?.label}
- Why these records matter for accountability
- What legal disclosures they are required to make

${form.investigationTypes.includes('campaign_finance') ? `
## CAMPAIGN FINANCE — WHERE TO LOOK
- FEC database (federal): fec.gov/data — exact search instructions
- ${form.level === 'state' ? `${form.state} state campaign finance database — name and URL` : 'State-level PAC and campaign databases'}
- OpenSecrets.org — how to use it for this subject
- FollowTheMoney.org — how to use it
- What to look for: large donors, bundlers, PAC connections, dark money organizations
- Red flags that indicate potential conflicts of interest
- How to trace dark money back to its source
` : ''}

${form.investigationTypes.includes('contracts') ? `
## GOVERNMENT CONTRACTS — WHERE TO LOOK
- USASpending.gov — exact search instructions for ${form.subjectName}
- SAM.gov — contractor registration and past performance
- FPDS (Federal Procurement Data System) if federal
- ${form.level === 'state' ? `${form.state} state procurement database — name and search tips` : ''}
- What to look for: no-bid contracts, sole-source awards, unusual price increases
- How to identify connected contractors (family, friends, donors)
- Red flags: contracts awarded to campaign donors, post-employment at contractors
` : ''}

${form.investigationTypes.includes('disclosures') ? `
## PERSONAL FINANCIAL DISCLOSURES — WHERE TO LOOK
- For federal officials: OGE public financial disclosure database (oge.gov)
- For members of Congress: clerk.house.gov or senate.gov disclosures
- ${form.level === 'state' ? `${form.state} state financial disclosure database` : ''}
- What is required to be disclosed: investments, income, assets, liabilities
- How to spot conflicts: investments in regulated industries, loans from donors
- What is NOT required to be disclosed and how to find it anyway
` : ''}

${form.investigationTypes.includes('spending') ? `
## GOVERNMENT SPENDING — WHERE TO LOOK
- USASpending.gov for federal agency spending
- Budget documents and appropriations (congress.gov, agency websites)
- Inspector General reports (each agency has one — list relevant IG office)
- GAO reports on this agency or program
- ${form.level === 'state' ? `${form.state} state budget transparency portal` : ''}
- What to look for: budget transfers, emergency spending, sole-source contracts
` : ''}

${form.investigationTypes.includes('lobbying') ? `
## LOBBYING — WHERE TO LOOK
- Senate lobbying disclosure database: lda.senate.gov
- OpenSecrets lobbying database
- Revolving door connections (lobbyists who were formerly government officials)
- What to look for: which industries lobby this official, meeting frequency
- How to cross-reference lobbyist spending with votes or policy decisions
` : ''}

${form.investigationTypes.includes('travel') ? `
## TRAVEL AND EXPENSES — WHERE TO LOOK
- Publicly funded travel is subject to FOIA
- For Congress: clerk.house.gov trip disclosures
- Per diem records and expense reports
- Conference attendance paid by third parties
- How to file a FOIA request for travel records
` : ''}

## HOW TO FILE FOIA REQUESTS FOR FINANCIAL RECORDS
- Which agencies to request records from
- Sample language for financial records FOIA requests
- What to ask for specifically
- Timeline expectations and appeal rights

## RED FLAGS — WHAT TO LOOK FOR
Specific patterns that indicate potential corruption or conflicts:
- Campaign donors who receive contracts
- Post-government employment at regulated entities
- Sudden unexplained wealth
- Voting patterns that align with donor interests
- Family members employed by contractors or lobbyists

## HOW TO DOCUMENT AND REPORT FINDINGS
- How to organize and verify what you find
- Which journalists cover this beat
- Which oversight bodies should receive your findings
- How to file a formal complaint if you find evidence of wrongdoing
- Whistleblower protections if you are an insider

## KEY DATABASES SUMMARY
Quick reference list of every database mentioned with URLs and what each contains.

Be specific — provide actual database names, URLs, and search instructions. This is a research roadmap, not general advice.`;

        try {
            const response = await fetch('/api/ai/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('boc_token')}` },
                body: JSON.stringify({ prompt, max_tokens: 3000 })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Generation failed');
            const text = data.text || '';
            if (!text) throw new Error('No response generated');
            setResult({ analysis: text, subjectName: form.subjectName });
            setStep(3);
        } catch (err) {
            setError('Failed to generate analysis. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const downloadWord = () => {
        downloadReportAsWord(
            result.analysis,
            `Follow-the-Money-${result.subjectName.replace(/[^a-z0-9]/gi,'-').slice(0,30)}`
        );
    };

    const reset = () => { setStep(1); setResult(null); setError(''); setForm({ subjectName: '', subjectType: '', level: 'federal', state: '', investigationTypes: [], context: '', specificConcern: '' }); };

    return (
        <>
            <Helmet><title>Follow the Money — By Our Consent</title></Helmet>
            <Nav />

            <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                    <Link to="/premium" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a9aaa', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>← Premium tools</Link>
                    <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a', marginTop: '10px' }}>Official accountability suite</div>
                    <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>Follow the Money</h1>
                    <p style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                        Name an official, agency, or contractor and get a complete roadmap to their financial records —
                        campaign donors, government contracts, personal disclosures, lobbying connections, and more.
                    </p>
                </div>
            </div>

            <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem' }}>
                <StepIndicator step={step} total={3} onStepClick={setStep} />

                {step === 1 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 1 — Who are you investigating?
                        </div>

                        {/* Help panel */}
                        <details style={{ marginBottom: '1.5rem', background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '3px' }}>
                            <summary style={{ padding: '10px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', fontWeight: 600, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                💡 How to get the best results
                            </summary>
                            <div style={{ padding: '0 14px 14px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.7 }}>
                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px', marginTop: '10px' }}>Use the official full name</div>
                                <div style={{ marginBottom: '6px' }}>Use the person's full legal name as it appears in public records — this ensures database searches return accurate results.</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', marginBottom: '10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Robert James Smith" not "Bob Smith" — campaign finance databases use full legal names.
                                </div>
                                <div style={{ fontWeight: 600, color: '#1e2a3a', marginBottom: '4px' }}>Specific concern — what triggered this investigation?</div>
                                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '8px 10px', fontSize: '11px', color: '#6a7080' }}>
                                    <strong style={{ color: '#1e2a3a' }}>Example:</strong> "Senator voted to deregulate the pharmaceutical industry three times after receiving $2.3M in pharma donations. I want to document the connection."
                                </div>
                            </div>
                        </details>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Full name of person, agency, or contractor *</label>
                            <input style={iStyle} type="text" placeholder="e.g. Robert James Smith, Department of Energy, Acme Defense Corp..."
                                value={form.subjectName} onChange={e => set('subjectName', e.target.value)} />
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Subject type *</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {SUBJECT_TYPES.map(s => (
                                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: form.subjectType === s.id ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.subjectType === s.id ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: form.subjectType === s.id ? 600 : 400 }}>{s.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{s.desc}</div>
                                        </div>
                                        <input type="radio" name="subjectType" checked={form.subjectType === s.id} onChange={() => set('subjectType', s.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0 }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>Level</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {[['federal','Federal'],['state','State / Local']].map(([val, label]) => (
                                    <button key={val} onClick={() => set('level', val)}
                                        style={{ flex: 1, padding: '8px', background: form.level === val ? '#8b1a1a' : '#f5f0e8', color: form.level === val ? '#f5f0e8' : '#4a5060', border: `0.5px solid ${form.level === val ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', cursor: 'pointer' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {form.level === 'state' && (
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={lStyle}>State</label>
                                <select style={iStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                                    <option value="">Select state...</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>What do you want to investigate? (select all that apply) *</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {INVESTIGATION_TYPES.map(i => (
                                    <label key={i.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: form.investigationTypes.includes(i.id) ? '#f0e8e0' : '#f5f0e8', border: `0.5px solid ${form.investigationTypes.includes(i.id) ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px', cursor: 'pointer' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{i.label}</div>
                                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '1px' }}>{i.desc}</div>
                                        </div>
                                        <input type="checkbox" checked={form.investigationTypes.includes(i.id)} onChange={() => toggleInv(i.id)}
                                            style={{ width: '16px', height: '16px', accentColor: '#8b1a1a', flexShrink: 0, cursor: 'pointer' }} />
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={lStyle}>What triggered this investigation? (optional)</label>
                            <textarea style={{ ...iStyle, resize: 'vertical' }} rows={3}
                                placeholder="What made you suspicious? A vote, a contract award, a news story, a conflict of interest you noticed?..."
                                value={form.context} onChange={e => set('context', e.target.value)} />
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={lStyle}>Specific concern or connection to investigate (optional)</label>
                            <input style={iStyle} type="text"
                                placeholder="e.g. Possible connection between campaign donors and no-bid contract awards..."
                                value={form.specificConcern} onChange={e => set('specificConcern', e.target.value)} />
                        </div>

                        <button style={{ ...btnPrimary, opacity: !canProceed ? 0.5 : 1 }} disabled={!canProceed} onClick={() => setStep(2)}>
                            Next: Review & generate →
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1.5rem' }}>
                            Step 2 — Review your request
                        </div>

                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px', fontFamily: 'sans-serif' }}>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Subject:</div>
                                <div style={{ color: '#1e2a3a' }}>{form.subjectName}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Type:</div>
                                <div style={{ color: '#1e2a3a' }}>{selectedSubjectType?.label}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Level:</div>
                                <div style={{ color: '#1e2a3a' }}>{form.level === 'federal' ? 'Federal' : `${form.state} State`}</div>
                                <div style={{ color: '#8a8070', fontWeight: 600 }}>Investigating:</div>
                                <div style={{ color: '#1e2a3a' }}>{INVESTIGATION_TYPES.filter(i => form.investigationTypes.includes(i.id)).map(i => i.label).join(', ')}</div>
                            </div>
                        </div>

                        {error && <div style={{ background: '#fdf0ee', border: '0.5px solid #d4a8a0', borderRadius: '3px', padding: '10px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#8b1a1a', marginBottom: '1rem' }}>{error}</div>}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
                            <button style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }} disabled={loading} onClick={generate}>
                                {loading ? 'Building your roadmap...' : 'Generate Investigation Roadmap →'}
                            </button>
                        </div>

                        {loading && (
                            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6 }}>
                                ⏳ Building your financial investigation roadmap with databases, search tips, and red flags...
                                <br />This may take 20-30 seconds.
                            </div>
                        )}
                    </div>
                )}

                {step === 3 && result && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>Investigation roadmap ready</div>
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '4px' }}>{result.subjectName}</div>
                            </div>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Roadmap</button>
                        </div>

                        <div style={{ background: '#fff', border: '0.5px solid #c8beaa', borderRadius: '3px', padding: '2rem', fontFamily: 'Georgia, serif', fontSize: '13px', lineHeight: 1.8, color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            {result.analysis.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) return <div key={i} style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: 'sans-serif', marginTop: i === 0 ? 0 : '1.5rem', marginBottom: '8px', paddingBottom: '6px', borderBottom: '0.5px solid #c8beaa' }}>{line.replace('## ', '')}</div>;
                                if (line.startsWith('- ') || line.startsWith('• ')) return <div key={i} style={{ paddingLeft: '1rem', marginBottom: '4px' }}>• {line.slice(2)}</div>;
                                if (!line.trim()) return <div key={i} style={{ height: '8px' }} />;
                                return <div key={i} style={{ marginBottom: '4px' }}>{line}</div>;
                            })}
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button onClick={downloadWord} style={btnPrimary}>⬇ Download Roadmap</button>
                            <button onClick={reset} style={btnSecondary}>New investigation</button>
                        </div>

                        <div style={{ marginTop: '1.5rem', padding: '10px 14px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '3px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', lineHeight: 1.6 }}>
                            This roadmap is for research and civic engagement purposes. Verify all findings through official sources before making public claims. If you find evidence of criminal conduct, consult an attorney before acting.
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

export default FollowTheMoneyPage;
